import { useCallback, useId, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types/field";
import { Button, EmptyState, PageHeader, Skeleton } from "@/components/ui";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import { accountClaimErrorKey } from "@/components/tester/claim/account-claim-error";
import { formatPremiseAddress } from "@/components/tester/claim/claim-reveal";
import { ClaimableDeviceRow } from "@/components/tester/claim/claimable-device-row";
import { LinkedAccountGone } from "@/components/tester/work/linked-account-gone";
import { mergeLinkedPremisePages } from "@/components/tester/work/linked-premise-reveal";
import { useAccountLinkClaim } from "@/hooks/use-account-link-claim";
import { useLinkedAccountPremise } from "@/hooks/use-linked-account-premise";
import { useToast } from "@/hooks/use-toast";

type ClaimableDevice = components["schemas"]["ClaimableDevice"];

export const Route = createFileRoute("/_company/my-work/accounts/$linkId/premises/$facilityId")({
  component: LinkedAccountPremiseDevicesPage,
});

const LOADING_ROW_COUNT = 3;

/**
 * One premise a linked account reaches, with the account's assemblies there
 * that the letter reveal would show — due within the lookahead or carrying
 * open utility work — each in the letter reveal's own row: the same states,
 * the same owning-party copy, and a Start where the row is claimable. "Load
 * more" walks the reveal's pages, which carry no total, so a full last page
 * is the only evidence of more.
 *
 * Start claims the assembly through the link and lands on the work it answers
 * with. A refusal keeps the tester HERE — the row's state moved under them,
 * which is the whole reason it was refused — with the sentence for it toasted
 * and the reveal re-read, so the row now says why; it never navigates to an
 * assignment that does not exist. Every Start is disabled while any claim is
 * in flight — a second claim would swallow the first's answer, which commits
 * server-side with no navigation or toast — and nothing is retried behind the
 * tester's back.
 *
 * A link the tester may no longer act on, or a premise the account does not
 * reach, is the API's one 404, read as "no longer active". Online only:
 * offline the shell holds this route behind its connection panel.
 */
function LinkedAccountPremiseDevicesPage() {
  const { linkId, facilityId } = Route.useParams();
  const { t } = useTranslation(["field-work", "common"]);
  const toast = useToast();
  const navigate = useNavigate();
  const titleId = useId();

  const reveal = useLinkedAccountPremise(linkId, facilityId);
  const { mutate: claim } = useAccountLinkClaim();
  const [claimingDeviceId, setClaimingDeviceId] = useState<string | null>(null);

  const premise = useMemo(() => mergeLinkedPremisePages(reveal.data?.pages ?? []), [reveal.data]);

  const handleStart = useCallback(
    (device: ClaimableDevice) => {
      setClaimingDeviceId(device.device_installation_id);
      claim(
        { linkId, deviceInstallationId: device.device_installation_id },
        {
          onSuccess: (assignment) => {
            void navigate({
              to: "/my-work/$assignmentId",
              params: { assignmentId: assignment.id },
            });
          },
          // The claim hook re-reads the reveal and the premise list on a
          // refusal; the tester stays on the row that now says why.
          onError: (error) => toast.error(accountClaimErrorKey(error)),
          onSettled: () => setClaimingDeviceId(null),
        },
      );
    },
    [claim, linkId, navigate, toast],
  );

  const backTo = `/my-work/accounts/${linkId}`;
  const backLabel = t("field-work:myWork.linkedAccounts.backToAccount");

  const error = reveal.error;
  if (error instanceof ApiError && error.status === 403) return <FieldAccessEnded />;
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="space-y-6 p-4 sm:p-8">
        <PageHeader
          title={t("field-work:myWork.linkedAccounts.reveal.fallbackTitle")}
          backTo={backTo}
          backLabel={backLabel}
        />
        <LinkedAccountGone />
      </div>
    );
  }
  if (reveal.isError) {
    return (
      <div className="flex min-h-64 items-center justify-center p-4 sm:p-8">
        <p className="text-danger">{t("common:errors.somethingWentWrong")}</p>
      </div>
    );
  }
  if (premise === null) {
    return (
      <div className="space-y-6 p-4 sm:p-8">
        <Skeleton width="240px" height="36px" />
        <div className="space-y-2">
          {Array.from({ length: LOADING_ROW_COUNT }).map((_, index) => (
            <Skeleton key={`device-row-${String(index)}`} height="72px" />
          ))}
        </div>
      </div>
    );
  }

  const address = formatPremiseAddress(premise);

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={premise.name}
        subtitle={
          address === ""
            ? premise.org_name
            : t("field-work:claim.reveal.premisePlace", { address, utility: premise.org_name })
        }
        backTo={backTo}
        backLabel={backLabel}
      />

      <section
        aria-labelledby={titleId}
        className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <h2 id={titleId} className="mb-4 text-lg font-semibold text-on-surface">
          {t("field-work:myWork.linkedAccounts.reveal.title")}
        </h2>

        {premise.devices.length === 0 ? (
          <EmptyState
            variant="inline"
            title={t("field-work:myWork.linkedAccounts.reveal.emptyTitle")}
            description={t("field-work:myWork.linkedAccounts.reveal.emptyBody", {
              utility: premise.org_name,
            })}
          />
        ) : (
          <div className="space-y-2">
            {premise.devices.map((device) => (
              <ClaimableDeviceRow
                key={device.device_installation_id}
                device={device}
                claimedAssignmentId={undefined}
                claiming={claimingDeviceId === device.device_installation_id}
                busy={claimingDeviceId !== null}
                onClaim={handleStart}
                actionLabelKey="field-work:myWork.linkedAccounts.reveal.start"
              />
            ))}
            {reveal.hasNextPage ? (
              <Button
                variant="secondary"
                className="w-full"
                loading={reveal.isFetchingNextPage}
                disabled={reveal.isFetchingNextPage}
                onClick={() => void reveal.fetchNextPage()}
              >
                {t("field-work:claim.reveal.loadMore")}
              </Button>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
